import { Combobox, NumberInput } from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { useTranslate } from '@ur-solutions/react-hooks'
import { DailyOvertimeEditor } from './helpers/daily-overtime-editor'
import { PeriodList } from './period-list'
import {
  SettingNote,
  SettingRow,
  SettingSwitch,
  SettingsSection,
} from './salary-settings-section'
import type { SalarySettingsTabProps } from './SalarySettingsFormView'

type OvertimeMode = '' | 'day' | 'week' | 'dayAndWeek' | 'month'

export const SalarySettingsOvertime: React.FC<SalarySettingsTabProps> = ({
  form,
}) => {
  const translations = useTranslate({
    overtime: 'common.overtime',
    overtimeMode: 'common.overtime-mode',
    none: 'common.none',
    day: 'common.day',
    week: 'common.week',
    month: 'common.month',
    dayAndWeek: 'settings.day-and-week-overtime',
    weekOvertimeDelayedOvertimeWarning:
      'settings.week-overtime-delayed-overtime-warning',
    usePlannerForOvertime: 'settings.use-planner-for-overtime',
    overtimeCalendarItemSalaryCode:
      'settings.overtime-calendar-item-salary-code',
    unplannedWorkOvertimeSalaryCode:
      'settings.unplanned-work-overtime-salary-code',
    dailyWorkLimit: 'settings.daily-work-limit',
    defaultSalaryCodeDayOvertime: 'settings.default-salary-code-day-overtime',
    periodWorkLimit: 'settings.period-work-limit',
    defaultSalaryCodeForPeriodOvertime:
      'settings.default-salary-code-for-period-overtime',
    calculateOvertimeInline: 'settings.calculate-overtime-inline',
    calculateOvertimeAsAddon: 'settings.calculate-overtime-as-addon',
    overtimeSalaryCodeBasedOnTimeOfDay:
      'salary.overtime-salary-code-based-on-time-of-day',

    undertime: 'common.undertime',
    undertimeExplanation: ['settings.undertime-explanation', { period: '' }],
    undertimePlanPeriodsExplanation:
      'settings.undertime-plan-periods-explanation',
    usePlannerForUndertime: 'settings.use-planner-for-undertime',
    usePlanPeriodsForUndertime: 'settings.use-plan-periods-for-undertime',
    undertimeLimit: 'settings.undertime-limit',
  })

  const { formValues, updateForm } = form
  const {
    useDayOvertime,
    useWeekOvertime,
    useMonthOvertime,
    usePlannerForOvertime: plannerForOvertimeActive,
    usePlannerForUndertime: plannerForUndertimeActive,
  } = formValues
  const plannerForOvertimeAllowed =
    useDayOvertime || useWeekOvertime || useMonthOvertime
  const planPeriodsForUndertimeActive =
    plannerForUndertimeActive && formValues.usePlanPeriodsForUndertime
  const undertimeAllowed = useWeekOvertime || useMonthOvertime
  const singleOvertimeMode =
    Number(useDayOvertime) +
      Number(useWeekOvertime) +
      Number(useMonthOvertime) ===
    1

  const overtimeMode: OvertimeMode =
    useWeekOvertime && useDayOvertime
      ? 'dayAndWeek'
      : useWeekOvertime
      ? 'week'
      : useDayOvertime
      ? 'day'
      : useMonthOvertime
      ? 'month'
      : ''

  function updateOvertimeMode(mode: OvertimeMode) {
    const nextUseWeek = mode === 'week' || mode === 'dayAndWeek'
    const nextUseDay = mode === 'day' || mode === 'dayAndWeek'
    const nextUseMonth = mode === 'month'
    const undertimeStillAllowed = nextUseWeek || nextUseMonth
    updateForm({
      useWeekOvertime: nextUseWeek,
      useDayOvertime: nextUseDay,
      useMonthOvertime: nextUseMonth,
      usePlannerForOvertime:
        nextUseDay || nextUseWeek || nextUseMonth
          ? formValues.usePlannerForOvertime
          : false,
      usePlannerForUndertime: undertimeStillAllowed
        ? formValues.usePlannerForUndertime
        : false,
      usePlanPeriodsForUndertime: undertimeStillAllowed
        ? formValues.usePlanPeriodsForUndertime
        : false,
      calculateOvertimeAsAddon:
        mode === 'dayAndWeek' ? false : formValues.calculateOvertimeAsAddon,
      calculateWeekOvertimeInline:
        mode === 'dayAndWeek' ? false : formValues.calculateWeekOvertimeInline,
    })
  }

  const dayOvertimeSalaryCodeRow = (
    <SettingRow label={translations.defaultSalaryCodeDayOvertime}>
      <SalaryCodeCombobox
        mode="single"
        clearable
        value={formValues.dayOvertimeSalaryCode ?? ''}
        onChange={code => updateForm({ dayOvertimeSalaryCode: code || null })}
      />
    </SettingRow>
  )

  return (
    <>
      <SettingsSection title={translations.overtime}>
        <SettingRow label={translations.overtimeMode}>
          <Combobox
            mode="single"
            noSearch
            items={[
              { value: '', label: translations.none },
              { value: 'day', label: translations.day },
              { value: 'week', label: translations.week },
              { value: 'dayAndWeek', label: translations.dayAndWeek },
              { value: 'month', label: translations.month },
            ]}
            value={overtimeMode}
            onChange={mode => updateOvertimeMode(mode as OvertimeMode)}
          />
        </SettingRow>
        {useWeekOvertime && useDayOvertime && (
          <SettingNote tone="warning">
            {translations.weekOvertimeDelayedOvertimeWarning}
          </SettingNote>
        )}

        {plannerForOvertimeAllowed && (
          <>
            <SettingSwitch
              label={translations.usePlannerForOvertime}
              checked={plannerForOvertimeActive}
              onChange={usePlannerForOvertime =>
                updateForm({ usePlannerForOvertime })
              }
            />
            <SettingRow label={translations.overtimeCalendarItemSalaryCode}>
              <SalaryCodeCombobox
                mode="single"
                clearable
                value={formValues.overtimeCalendarItemSalaryCode ?? ''}
                onChange={code =>
                  updateForm({ overtimeCalendarItemSalaryCode: code || null })
                }
              />
            </SettingRow>
          </>
        )}
        {plannerForOvertimeActive && (
          <SettingRow label={translations.unplannedWorkOvertimeSalaryCode}>
            <SalaryCodeCombobox
              mode="single"
              clearable
              value={formValues.unplannedWorkOvertimeSalaryCode ?? ''}
              onChange={code =>
                updateForm({ unplannedWorkOvertimeSalaryCode: code || null })
              }
            />
          </SettingRow>
        )}

        {useDayOvertime && useWeekOvertime && (
          <>
            {!plannerForOvertimeActive && (
              <SettingRow label={translations.dailyWorkLimit}>
                <NumberInput
                  value={formValues.dailyWorkLimit}
                  min={0}
                  max={24}
                  decimalScale={2}
                  onChange={value => updateForm({ dailyWorkLimit: value ?? 0 })}
                />
              </SettingRow>
            )}
            {dayOvertimeSalaryCodeRow}
          </>
        )}
        {useDayOvertime &&
          plannerForOvertimeActive &&
          !useWeekOvertime &&
          dayOvertimeSalaryCodeRow}
        {useDayOvertime && !plannerForOvertimeActive && !useWeekOvertime && (
          <div className="px-5 py-4 sm:px-6">
            <DailyOvertimeEditor
              dailyWorkLimit={formValues.dailyWorkLimit}
              onDailyWorkLimitChange={dailyWorkLimit =>
                updateForm({ dailyWorkLimit })
              }
              dayOvertimeSalaryCode={formValues.dayOvertimeSalaryCode}
              onDayOvertimeSalaryCodeChange={dayOvertimeSalaryCode =>
                updateForm({ dayOvertimeSalaryCode })
              }
              overtimeLimits={formValues.overtimeLimits}
              onOvertimeLimitsChange={overtimeLimits =>
                updateForm({ overtimeLimits })
              }
              tiersDisabled={!formValues.calculateWeekOvertimeInline}
            />
          </div>
        )}

        {(useWeekOvertime || useMonthOvertime) && (
          <>
            {!plannerForOvertimeActive && (
              <SettingRow label={translations.periodWorkLimit}>
                <NumberInput
                  value={formValues.periodWorkLimit}
                  min={0}
                  max={24 * 7}
                  decimalScale={2}
                  onChange={value =>
                    updateForm({ periodWorkLimit: value ?? 0 })
                  }
                />
              </SettingRow>
            )}
            <SettingRow label={translations.defaultSalaryCodeForPeriodOvertime}>
              <SalaryCodeCombobox
                mode="single"
                clearable
                value={formValues.weekOvertimeSalaryCode ?? ''}
                onChange={code =>
                  updateForm({ weekOvertimeSalaryCode: code || null })
                }
              />
            </SettingRow>
          </>
        )}

        {singleOvertimeMode && (
          <SettingSwitch
            label={translations.calculateOvertimeInline}
            checked={formValues.calculateWeekOvertimeInline}
            onChange={calculateWeekOvertimeInline =>
              updateForm({ calculateWeekOvertimeInline })
            }
          />
        )}
        {singleOvertimeMode && formValues.calculateWeekOvertimeInline && (
          <SettingSwitch
            label={translations.calculateOvertimeAsAddon}
            checked={formValues.calculateOvertimeAsAddon}
            onChange={calculateOvertimeAsAddon =>
              updateForm({ calculateOvertimeAsAddon })
            }
          />
        )}
      </SettingsSection>

      {singleOvertimeMode && formValues.calculateWeekOvertimeInline && (
        <SettingsSection
          title={translations.overtimeSalaryCodeBasedOnTimeOfDay}
        >
          <div className="px-5 py-4 sm:px-6">
            <PeriodList form={form} field="periodOvertimes" />
          </div>
        </SettingsSection>
      )}

      {undertimeAllowed && (
        <SettingsSection title={translations.undertime}>
          <SettingNote>
            {planPeriodsForUndertimeActive
              ? translations.undertimePlanPeriodsExplanation
              : translations.undertimeExplanation({
                  period: useMonthOvertime
                    ? translations.month
                    : translations.week,
                })}
          </SettingNote>
          <SettingSwitch
            label={translations.usePlannerForUndertime}
            checked={plannerForUndertimeActive}
            onChange={usePlannerForUndertime =>
              updateForm({
                usePlannerForUndertime,
                // Plan-period undertime requires the planner flag on the
                // backend, so it cannot stay on without it
                usePlanPeriodsForUndertime: usePlannerForUndertime
                  ? formValues.usePlanPeriodsForUndertime
                  : false,
              })
            }
          />
          {plannerForUndertimeActive && (
            <SettingSwitch
              label={translations.usePlanPeriodsForUndertime}
              checked={formValues.usePlanPeriodsForUndertime}
              onChange={usePlanPeriodsForUndertime =>
                updateForm({ usePlanPeriodsForUndertime })
              }
            />
          )}
          {!plannerForUndertimeActive && (
            <SettingRow label={translations.undertimeLimit}>
              <NumberInput
                value={formValues.undertimeLimit}
                min={0}
                max={24 * 7}
                decimalScale={2}
                onChange={value => updateForm({ undertimeLimit: value ?? 0 })}
              />
            </SettingRow>
          )}
        </SettingsSection>
      )}
    </>
  )
}
